import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useAuth } from '../../hooks/useAuth';
import { useDarkMode } from '../../hooks/useDarkMode';
import { Moon, Sun, ChevronLeft, Home, Menu, Bell } from 'lucide-react';
import api from '../../lib/api';
import AiChatWidget from '../chat/AiChatWidget';
import TrialBanner from './TrialBanner';
import CreditBalanceBanner from './CreditBalanceBanner';
import ImpersonationBanner from './ImpersonationBanner';
import LockGate from './LockGate';
import NewDonationDialog from '../NewDonationDialog';
import CreateDocumentDialog from '../CreateDocumentDialog';
import CreatePriceQuoteDialog from '../CreatePriceQuoteDialog';
import PaymentLinkDialog from '../PaymentLinkDialog';

interface MainLayoutProps {
  children: React.ReactNode;
}

const routeLabels: Record<string, string> = {
  '/': 'דשבורד',
  '/dashboard': 'דשבורד',
  '/contacts': 'אנשי קשר',
  '/import/contacts': 'ייבוא אנשי קשר',
  '/donations': 'תרומות',
  '/memberships': 'הוראות קבע',
  '/campaigns': 'קמפיינים',
  '/events': 'אירועים',
  '/volunteers': 'מתנדבים',
  '/volunteer-projects': 'פרויקטי התנדבות',
  '/training': 'קבוצות אימון',
  '/volunteer-form-editor': 'עריכת טופס מתנדב',
  '/shifts': 'משמרות',
  '/grants': 'מענקים',
  '/reports': 'דוחות',
  '/payments': 'תשלומים',
  '/finances': 'פיננסים',
  '/budget': 'תקציב',
  '/workplan': 'תוכנית עבודה',
  '/projects': 'פרויקטים',
  '/project-forms': 'טפסי פרויקטים',
  '/beneficiaries': 'מוטבים',
  '/expenses': 'הוצאות',
  '/payment-demands': 'דרישות תשלום',
  '/masav': 'מס״ב — ניכוי ישיר',
  '/masav/debits': 'מס״ב — חיובים',
  '/masav/returns': 'מס״ב — החזרות',
  '/masav/credits': 'מס״ב — זיכויים',
  '/settings/masav': 'הגדרות מס״ב',
  '/mailing': 'דיוור מייל',
  '/whatsapp/templates': 'תבניות וואטסאפ',
  '/delivery': 'הפצה',
  '/tools': 'כלים',
  '/tools/new': 'כלי חדש',
  '/block-editor': 'עורך בלוקים',
  '/easy-landing': 'עורך דף נחיתה',
  '/calendar': 'לוח שנה',
  '/settings': 'הגדרות',
  '/profile': 'הפרופיל שלי',
  '/knowledge': 'מרכז ידע',
  '/billing': 'חבילה וחיוב',
  '/permissions': 'הרשאות',
  '/agents/tasks': 'לוח משימות',
};

/** זמן יחסי בעברית — "לפני 5 דקות" / "לפני שעתיים" / תאריך מלא לישנות. */
function relativeTime(d: string | Date): string {
  const t = new Date(d).getTime();
  if (isNaN(t)) return '';
  const diffSec = Math.round((t - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('he-IL', { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.trunc(diffSec / 1), 'second');
  if (abs < 3600) return rtf.format(Math.trunc(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.trunc(diffSec / 3600), 'hour');
  if (abs < 86400 * 7) return rtf.format(Math.trunc(diffSec / 86400), 'day');
  return new Date(t).toLocaleDateString('he-IL');
}

const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

/** 🔔 פעמון התראות — polling כל דקה, פופאובר עם סימון-כנקרא וניווט. */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const res = await api.get('/notifications?unreadOnly=1&limit=15');
      setItems(res.data?.notifications || []);
      setUnreadCount(res.data?.unreadCount || 0);
    } catch { /* noop — הפעמון לא מפיל את ההדר */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // סגירה בלחיצה מחוץ לפופאובר
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const onItemClick = async (n: any) => {
    try { await api.post(`/notifications/${n._id}/read`); } catch { /* noop */ }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAllRead = async () => {
    try { await api.post('/notifications/read-all'); } catch { /* noop */ }
    load();
  };

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        aria-label="התראות"
        className="relative p-2 rounded-xl hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] text-[#49454F] dark:text-[#CAC4D0] transition-all duration-200"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 left-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 z-[60] w-80 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl border border-[#E0E0E0] dark:border-[#2C2C2C] overflow-hidden" dir="rtl">
          <div className="px-4 py-2.5 border-b border-[#F0F0F0] dark:border-[#2C2C2C] font-semibold text-sm text-[#1C1B1F] dark:text-white">
            🔔 התראות
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[#9E9E9E]">אין התראות חדשות</div>
            ) : items.map((n: any) => (
              <button
                key={n._id}
                onClick={() => onItemClick(n)}
                className="w-full flex items-start gap-2.5 px-4 py-2.5 hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] transition-colors text-right border-b border-[#F7F7F7] dark:border-[#262626] last:border-b-0"
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT[n.severity] || SEVERITY_DOT.info}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-[#1C1B1F] dark:text-white truncate">{n.title}</span>
                  {n.body && <span className="block text-xs text-[#49454F] dark:text-[#CAC4D0] leading-snug line-clamp-2">{n.body}</span>}
                  <span className="block text-[10px] text-[#9E9E9E] mt-0.5">{relativeTime(n.createdAt)}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="border-t border-[#F0F0F0] dark:border-[#2C2C2C]">
            <button
              onClick={markAllRead}
              className="w-full px-4 py-2.5 text-xs font-semibold text-[#1565C0] dark:text-[#90CAF9] hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] transition-colors"
            >
              סמן הכל כנקרא
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/** מחלץ ראשי תיבות משם מלא */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { logout, user } = useAuth();
  const { isDark, toggle } = useDarkMode();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newDonationOpen, setNewDonationOpen] = useState(false);
  const [createDocumentOpen, setCreateDocumentOpen] = useState(false);
  const [priceQuoteOpen, setPriceQuoteOpen] = useState(false);
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // סגור dropdown בלחיצה מחוץ
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // טיפול בדפים דינמיים (למשל /contacts/:id, /campaigns/:id/dashboard)
  const getDynamicLabel = (pathname: string): string => {
    if (routeLabels[pathname]) return routeLabels[pathname];
    if (/^\/contacts\/[^/]+$/.test(pathname)) return 'פרטי איש קשר';
    if (/^\/campaigns\/[^/]+\/dashboard$/.test(pathname)) return 'דשבורד קמפיין';
    if (/^\/tools\/[^/]+\/edit$/.test(pathname)) return 'עריכת כלי';
    if (/^\/tools\/[^/]+\/submissions$/.test(pathname)) return 'הגשות';
    return 'עמוד';
  };
  const currentLabel = getDynamicLabel(location.pathname);
  const isHome = location.pathname === '/';

  return (
    <LockGate>
    <div className={isDark ? 'dark' : ''}>
      {/* Impersonation banner — super-admin only, sticky top */}
      <ImpersonationBanner />
      {/* Trial Banner — sticky top, above everything */}
      <TrialBanner />
      <div
        dir="rtl"
        className="flex h-screen bg-[#FAFAFA] dark:bg-[#121212] overflow-hidden transition-colors duration-300"
      >
        {/* Sidebar — collapsible on mobile, always visible on desktop */}
        <Sidebar
          onLogout={logout}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Header */}
          <header className="bg-white dark:bg-[#1E1E1E] border-b border-[#E0E0E0] dark:border-[#2C2C2C] px-3 md:px-5 py-3 flex items-center gap-2 md:gap-3 flex-shrink-0 shadow-sm transition-colors duration-300">

            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-xl hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] text-[#49454F] dark:text-[#CAC4D0] flex-shrink-0 transition-all duration-200"
              aria-label="תפריט"
            >
              <Menu size={22} />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm flex-1">
              <Home size={14} className="text-[#49454F] dark:text-[#CAC4D0]" />
              {!isHome && (
                <>
                  <ChevronLeft size={13} className="text-[#BDBDBD] dark:text-[#3C3C3C] rotate-180" />
                  <span className="font-semibold text-[#1565C0] dark:text-[#90CAF9]">
                    {currentLabel}
                  </span>
                </>
              )}
              {isHome && (
                <span className="font-semibold text-[#1565C0] dark:text-[#90CAF9]">
                  דשבורד
                </span>
              )}
            </div>

            {/* Notifications bell */}
            <NotificationBell />

            {/* Dark mode toggle */}
            <button
              onClick={toggle}
              className="p-2 rounded-xl hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] text-[#49454F] dark:text-[#CAC4D0] flex-shrink-0 transition-all duration-200"
              aria-label="מצב כהה"
            >
              {isDark ? <Sun size={20} className="text-amber-400" /> : <Moon size={20} />}
            </button>

            {/* Avatar + Dropdown */}
            {user && (
              <div className="relative flex-shrink-0" ref={avatarRef}>
                <button
                  onClick={() => setAvatarOpen((v) => !v)}
                  aria-label="פרופיל משתמש"
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1565C0] to-[#6200EA] flex items-center justify-center text-white text-sm font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden border-2 border-white dark:border-[#2C2C2C]"
                >
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <span>{getInitials(user.name)}</span>
                  )}
                </button>
                {avatarOpen && (
                  <div className="absolute left-0 top-full mt-2 z-50 w-52 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl border border-[#E0E0E0] dark:border-[#2C2C2C] overflow-hidden" dir="rtl">
                    <div className="px-4 py-3 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                      <div className="font-semibold text-sm text-[#1C1B1F] dark:text-white">{user.name}</div>
                      {user.orgName && <div className="text-xs text-[#9E9E9E] mt-0.5">{user.orgName}</div>}
                    </div>
                    {[
                      { path: '/profile', label: 'הפרופיל שלי', icon: '👤' },
                      { path: '/settings', label: 'הגדרות משתמש', icon: '⚙️' },
                      { path: '/billing', label: 'חבילה וחיוב', icon: '💳' },
                    ].map((item) => (
                      <button key={item.path} onClick={() => { navigate(item.path); setAvatarOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#49454F] dark:text-[#CAC4D0] hover:bg-[#F5F5F5] dark:hover:bg-[#2C2C2C] transition-colors text-right"
                      >
                        <span>{item.icon}</span>{item.label}
                      </button>
                    ))}
                    <div className="border-t border-[#F0F0F0] dark:border-[#2C2C2C]">
                      <button onClick={() => { logout(); navigate('/login'); setAvatarOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#B00020] dark:text-[#CF6679] hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-right"
                      >
                        <span>🚪</span>התנתק
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </header>

          {/* ── Global Sticky Quick Actions Bar ── */}
          <div className="bg-white/95 dark:bg-[#1E1E1E]/95 backdrop-blur-md border-b border-[#E8E8E8] dark:border-[#2C2C2C] px-4 sm:px-6 py-2 flex-shrink-0" dir="rtl">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              <span className="text-xs font-semibold text-[#BDBDBD] whitespace-nowrap ml-1">⚡</span>
              <button onClick={() => navigate('/campaigns')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1565C0] hover:bg-[#0D47A1] text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-all shadow-sm active:scale-95">
                🚀 קמפיין חדש
              </button>
              <button onClick={() => setNewDonationOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00897B] hover:bg-[#00695C] text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-all shadow-sm active:scale-95">
                ❤️ תרומה חדשה
              </button>
              <button onClick={() => navigate('/expenses')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F57C00] hover:bg-[#E65100] text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-all shadow-sm active:scale-95">
                🧾 הכנס חשבונית/קבלה
              </button>
              <button onClick={() => navigate('/contacts')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6200EA] hover:bg-[#4527A0] text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-all shadow-sm active:scale-95">
                👤 איש קשר חדש
              </button>
              <button onClick={() => navigate('/events')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F5F5F5] dark:bg-[#2C2C2C] hover:bg-[#E0E0E0] dark:hover:bg-[#3C3C3C] text-[#49454F] dark:text-[#CAC4D0] text-xs font-semibold rounded-lg whitespace-nowrap transition-all">
                📅 אירוע חדש
              </button>
              <button onClick={() => setCreateDocumentOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3949AB] hover:bg-[#283593] text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-all shadow-sm active:scale-95">
                🧾 יצירת מסמך חשבונאי
              </button>
              <button onClick={() => setPaymentLinkOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#5E35B1] hover:bg-[#4527A0] text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-all shadow-sm active:scale-95">
                🔗 טופס תשלום
              </button>
            </div>
          </div>

          {/* Content */}
          <main className="flex-1 overflow-hidden bg-[#FAFAFA] dark:bg-[#121212] transition-colors duration-300 flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6 flex flex-col">
              {children}
            </div>
            <div className="text-center py-2 text-xs text-[#9E9E9E] dark:text-[#555] flex-shrink-0">
              v4.0.2
            </div>
          </main>
        </div>
      </div>

      {/* AI Chat Widget — float button, bottom-left (RTL) */}
      <AiChatWidget />

      {/* Shared "New Donation" dialog — opened from the AppBar button on any page. */}
      <NewDonationDialog open={newDonationOpen} onClose={() => setNewDonationOpen(false)} />

      {/* Accounting document (קבלה / דרישת תשלום) + standalone payment link —
          opened from the AppBar buttons on any page. */}
      <CreateDocumentDialog
        open={createDocumentOpen}
        onClose={() => setCreateDocumentOpen(false)}
        onSelectPriceQuote={() => { setCreateDocumentOpen(false); setPriceQuoteOpen(true); }}
      />
      <CreatePriceQuoteDialog open={priceQuoteOpen} onClose={() => setPriceQuoteOpen(false)} />
      <PaymentLinkDialog open={paymentLinkOpen} onClose={() => setPaymentLinkOpen(false)} />
    </div>
    </LockGate>
  );
};

export default MainLayout;
